import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://5vix.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '5vix',
  url: siteUrl,
  description: 'Integrated gym management system for membership, scheduling, billing, attendance, and analytics.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+201044553254',
    contactType: 'customer support',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: [
    'https://www.instagram.com/5vixofficial/',
    'https://www.tiktok.com/@5vixofficial',
    'https://www.youtube.com/@5vix-c3p',
    'https://www.linkedin.com/in/5vix-software-12058b363/',
  ],
};

const sectionIcons: Record<number, string> = {
  1: 'ri-file-list-line',
  2: 'ri-settings-line',
  3: 'ri-share-line',
  4: 'ri-lock-line',
  5: 'ri-database-2-line',
  6: 'ri-user-settings-line',
  7: 'ri-links-line',
  8: 'ri-refresh-line',
  9: 'ri-mail-line',
};

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const pageJsonLd = [
    organizationSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy - 5vix',
      description: 'Privacy policy for 5vix gym management platform covering data collection, usage, security, and user rights.',
    },
  ];

  return (
    <div className="bg-background-50 min-h-screen">
      <JsonLd data={pageJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[280px] md:min-h-[320px] flex items-center overflow-hidden bg-background-100">
        <div className="w-full px-4 md:px-6 py-14 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-primary-600 text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">
              {t('privacy.title')}
            </h1>
            <p className="text-foreground-500 text-sm">
              {t('privacy.lastUpdated')}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-foreground-700 text-sm leading-relaxed">
              {t('privacy.introduction')}
            </p>
          </div>

          {/* Section 1 */}
          <div className="mb-12">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6 flex items-center gap-3">
              <span className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                <i className={`${sectionIcons[1]} text-primary-600`}></i>
              </span>
              {t('privacy.section1.title')}
            </h2>
            <div className="pl-12 rtl:pl-0 rtl:pr-12 space-y-5">
              {[1, 2, 3].map((i) => (
                <div key={i}>
                  <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2">
                    {t(`privacy.section1.subtitle${i}`)}
                  </h3>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    {t(`privacy.section1.content${i}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Sections 2-8 */}
          {[2, 3, 4, 5, 6, 7, 8].map((n) => (
            <div key={n} className="mb-12">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6 flex items-center gap-3">
                <span className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                  <i className={`${sectionIcons[n]} text-primary-600`}></i>
                </span>
                {t(`privacy.section${n}.title`)}
              </h2>
              <div className="pl-12 rtl:pl-0 rtl:pr-12">
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  {t(`privacy.section${n}.content`)}
                </p>
                {(n === 2 || n === 6) && (
                  <ul className="space-y-2">
                    {(n === 2 ? [1, 2, 3, 4, 5] : [1, 2, 3, 4]).map((i) => (
                      <li key={i} className="flex items-start text-foreground-700 text-sm">
                        <span className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 shrink-0">
                          <i className="ri-check-line text-primary-500"></i>
                        </span>
                        <span>{t(`privacy.section${n}.item${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}

          {/* Section 9 - Contact */}
          <div className="bg-primary-50 rounded-2xl p-8 border border-primary-100/60">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-3">
              <span className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center shrink-0">
                <i className="ri-whatsapp-line text-background-50"></i>
              </span>
              {t('privacy.section9.title')}
            </h2>
            <p className="text-foreground-700 text-sm leading-relaxed mb-6">
              {t('privacy.section9.content')}
            </p>
            <div className="space-y-3">
              <div className="flex items-center text-foreground-700 text-sm">
                <span className="w-5 h-5 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3 shrink-0">
                  <i className="ri-whatsapp-line text-primary-500"></i>
                </span>
                <a href="https://wa.me/+201044553254" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-primary-600 transition-colors">+201044553254</a>
              </div>
              <div className="flex items-center text-foreground-700 text-sm">
                <span className="w-5 h-5 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3 shrink-0">
                  <i className="ri-phone-line text-primary-500"></i>
                </span>
                <a href="tel:201044553254" className="hover:text-primary-600 transition-colors">01044553254</a>
              </div>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-5">
            {t('privacy.cta.title')}
          </h2>
          <p className="text-background-50/85 text-base md:text-lg mb-8 leading-relaxed">
            {t('privacy.cta.subtitle')}
          </p>
          <Link
            to="/#register"
            className="inline-block bg-background-50 text-primary-600 px-10 py-3.5 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
          >
            {t('privacy.cta.button')}
          </Link>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
}